import { useCustomFieldDefinitions } from "./useCustomFields";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

type Values = Record<string, unknown>;

/**
 * Édite `custom_fields` (jsonb) d'une entité à partir des définitions posées
 * dans Paramètres. Rien n'est codé en dur : ajouter un champ = une ligne en base.
 */
export function CustomFieldsEditor({ entity, value, onChange }: { entity: string; value: Values; onChange: (v: Values) => void }) {
  const { data: defs } = useCustomFieldDefinitions(entity);
  if (!defs || defs.length === 0) return null;
  const set = (k: string, v: unknown) => onChange({ ...value, [k]: v });

  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {defs.map((d) => {
        const v = value[d.key];
        const id = `cf-${d.key}`;
        const options = Array.isArray(d.options) ? (d.options as string[]) : [];
        return (
          <div key={d.id} className="space-y-1.5">
            <Label htmlFor={id}>{d.label}{d.required && " *"}</Label>
            {d.type === "boolean" ? (
              <div className="pt-1"><Switch id={id} checked={Boolean(v)} onCheckedChange={(c) => set(d.key, c)} /></div>
            ) : d.type === "select" ? (
              <Select value={(v as string) ?? ""} onValueChange={(s) => set(d.key, s)}>
                <SelectTrigger id={id}><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>{options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
              </Select>
            ) : (
              <Input
                id={id}
                type={d.type === "number" ? "number" : d.type === "date" ? "date" : d.type === "url" ? "url" : "text"}
                value={(v as string | number | undefined) ?? ""}
                onChange={(e) => set(d.key, d.type === "number" ? (e.target.value === "" ? null : Number(e.target.value)) : e.target.value)}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

/** Affichage en lecture des champs personnalisés. */
export function CustomFieldsView({ entity, value }: { entity: string; value: Values }) {
  const { data: defs } = useCustomFieldDefinitions(entity);
  if (!defs || defs.length === 0) return null;
  return (
    <dl className="grid gap-x-6 gap-y-2 sm:grid-cols-2">
      {defs.map((d) => {
        const v = value[d.key];
        const text = v === null || v === undefined || v === "" ? "—" : typeof v === "boolean" ? (v ? "Oui" : "Non") : String(v);
        return (
          <div key={d.id} className="flex justify-between gap-4 border-b border-border/60 py-1.5 text-sm">
            <dt className="text-muted-foreground">{d.label}</dt>
            <dd className="text-right">{text}</dd>
          </div>
        );
      })}
    </dl>
  );
}
